import { PART_VARIANTS } from './portraitRenderer';

const CHEST_SIZES = ['A', 'B', 'C', 'D', 'E', 'F'];
const BUTTOCKS = ['flat', 'modest', 'rounded', 'full', 'firm', 'prominent'];

// ~100 reference colors covering the full spectrum, matched by nearest RGB distance
const COLOR_PALETTE = [
  ['#0a0a0a', 'jet black'],    ['#1a1a1a', 'black'],         ['#2d2d2d', 'dark charcoal'],
  ['#4a4a4a', 'charcoal'],     ['#6e6e6e', 'dim gray'],      ['#929292', 'gray'],
  ['#b0b0b0', 'silver gray'],  ['#c8c8c8', 'light gray'],    ['#e0e0e0', 'platinum'],
  ['#f5f5f5', 'white'],        ['#fffde7', 'ivory'],

  // reds
  ['#8b0000', 'dark red'],     ['#c0392b', 'crimson'],       ['#e74c3c', 'red'],
  ['#ff6b6b', 'coral red'],    ['#ff8a80', 'light red'],

  // oranges / browns
  ['#4e1f00', 'dark brown'],   ['#6d3b00', 'deep brown'],    ['#8b4513', 'saddlebrown'],
  ['#a0522d', 'sienna'],       ['#c46a2d', 'chestnut'],      ['#d2691e', 'chocolate'],
  ['#e07b39', 'auburn'],       ['#e8955a', 'caramel'],       ['#f0a875', 'peach brown'],
  ['#e67e22', 'orange'],       ['#f39c12', 'amber'],         ['#f5b041', 'warm amber'],

  // yellows / blondes
  ['#c8a84b', 'dark blonde'],  ['#d4a843', 'golden brown'],  ['#e2c068', 'dirty blonde'],
  ['#f0d080', 'blonde'],       ['#f7e4a0', 'light blonde'],  ['#fef9c3', 'platinum blonde'],
  ['#f1c40f', 'yellow'],       ['#f9e04b', 'bright yellow'],

  // greens
  ['#1a4d1a', 'dark forest green'], ['#27ae60', 'green'],   ['#2ecc71', 'emerald'],
  ['#58d68d', 'mint green'],   ['#a9dfbf', 'pale green'],    ['#7dcea0', 'sage'],
  ['#1abc9c', 'teal green'],   ['#16a085', 'dark teal'],     ['#0e6655', 'deep teal'],

  // cyans / teals
  ['#17a589', 'teal'],         ['#1dd2af', 'turquoise'],     ['#76d7c4', 'light teal'],
  ['#85c1e9', 'powder blue'],  ['#5dade2', 'sky blue'],

  // blues
  ['#1a2980', 'deep blue'],    ['#1a6fa8', 'dark blue'],     ['#2980b9', 'blue'],
  ['#3498db', 'cerulean'],     ['#7fb3d3', 'steel blue'],    ['#aed6f1', 'light blue'],
  ['#d6eaf8', 'pale blue'],    ['#1b4f72', 'navy'],          ['#2c3e7a', 'midnight blue'],

  // purples / violets
  ['#4a235a', 'deep purple'],  ['#6c3483', 'dark purple'],   ['#8e44ad', 'purple'],
  ['#9b59b6', 'violet'],       ['#a569bd', 'medium purple'], ['#c39bd3', 'light purple'],
  ['#d2b4de', 'lavender'],     ['#e8daef', 'pale lavender'],
  ['#5b2c6f', 'plum'],         ['#7d3c98', 'amethyst'],

  // pinks / magentas
  ['#7b241c', 'dark rose'],    ['#c0392b', 'deep rose'],     ['#e74c8b', 'hot pink'],
  ['#ff69b4', 'pink'],         ['#ffb3d1', 'light pink'],    ['#fce4ec', 'pale pink'],
  ['#e91e63', 'magenta pink'], ['#c2185b', 'deep pink'],     ['#ad1457', 'raspberry'],
  ['#f48fb1', 'rose pink'],    ['#f8bbd9', 'baby pink'],

  // specialty hair colors
  ['#b5651d', 'light brown'],  ['#deb887', 'burlywood'],     ['#d2a679', 'sandy brown'],
  ['#c19a6b', 'camel'],        ['#a67c52', 'warm brown'],
];

function hexToRgb(hex) {
  return [parseInt(hex.slice(1,3),16), parseInt(hex.slice(3,5),16), parseInt(hex.slice(5,7),16)];
}

export function colorName(hex) {
  const [r, g, b] = hexToRgb(hex);
  let best = COLOR_PALETTE[0][1];
  let bestDist = Infinity;
  for (const [ref, name] of COLOR_PALETTE) {
    const [rr, rg, rb] = hexToRgb(ref);
    const dist = (r-rr)**2 + (g-rg)**2 + (b-rb)**2;
    if (dist < bestDist) { bestDist = dist; best = name; }
  }
  return best;
}

const FIRST_NAMES = [
  'Ayla', 'Sera', 'Lirien', 'Celia', 'Naia', 'Vena', 'Isla', 'Tara', 'Kira',
  'Luma', 'Ysel', 'Dara', 'Oria', 'Seli', 'Vira', 'Nela', 'Cora', 'Aina',
  'Reia', 'Lyra', 'Sora', 'Dela', 'Teia', 'Wren', 'Ara', 'Nore', 'Elia',
  'Mael', 'Vael', 'Runa', 'Idris', 'Lenne', 'Syra', 'Cael', 'Oryn',
];

const FAMILY_NAMES = [
  'Ashmore', 'Thorn', 'Crestfall', 'Dunmore', 'Silvar', 'Ironwood', 'Gale',
  'Maren', 'Holst', 'Veyne', 'Crain', 'Lorne', 'Ashfen', 'Stonar', 'Halven',
  'Duskmore', 'Vayne', 'Merrow', 'Coldfen', 'Brackwell', 'Evander', 'Greystone',
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomHsl(hRange, sRange, lRange) {
  const h = randInt(...hRange) / 360;
  const s = randInt(...sRange) / 100;
  const l = randInt(...lRange) / 100;
  const [r, g, b] = hslToRgb(h, s, l);
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function hslToRgb(h, s, l) {
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1/3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1/3) * 255),
  ];
}

function generateParts() {
  const hairBackNone = Math.random() < 0.25;
  const hairTailIdx = hairBackNone
    ? randInt(0, PART_VARIANTS.hair_tail - 1)
    : (Math.random() < 0.05 ? randInt(0, PART_VARIANTS.hair_tail - 1) : -1);

  return {
    hair_tail:    hairTailIdx,
    hair_back:    hairBackNone ? -1 : randInt(0, PART_VARIANTS.hair_back - 1),
    face:         0,
    hair_base:    0,
    eyes:         randInt(0, PART_VARIANTS.eyes - 1),
    ears:         0,
    hair_sides:   randInt(0, PART_VARIANTS.hair_sides - 1),
    hair_fringes: randInt(0, PART_VARIANTS.hair_fringes - 1),
    brows:        0,
  };
}

export function generateCharacter(locationIds, idPrefix = 'npc') {
  const id = `${idPrefix}_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
  const name = `${pick(FIRST_NAMES)} ${pick(FAMILY_NAMES)}`;
  const location = pick(locationIds);
  const hairColor = randomHsl([0, 360], [30, 80], [25, 70]);
  const eyeColor  = randomHsl([0, 360], [40, 90], [35, 65]);

  return {
    id,
    name,
    maxHp: 80,
    _generated: true,
    location,
    hp: 80,
    relationship: 0,
    awareness: 'unaware',
    flags: [],
    itinerary: [],
    parts: generateParts(),
    hairColor,
    eyeColor,
    appearance: {
      hairColorName: colorName(hairColor),
      eyeColorName:  colorName(eyeColor),
      chest:         pick(CHEST_SIZES),
      buttocks:      pick(BUTTOCKS),
    },
  };
}

export function generateCharacters(count, locationIds) {
  return Array.from({ length: count }, () => generateCharacter(locationIds));
}
